import { useCallback, type KeyboardEvent, type ReactNode, type RefObject } from 'react'
import { useAevocamPlayer } from './useAevocamPlayer'
import './AevocamPlayerShell.css'

/** Shift + arrow pan step when not in fit zoom (matches drag axis: right/down increase `panX` / `panY`). */
const SHIFT_ARROW_PAN_PX = 100

/**
 * Outer wrapper from {@link AevocamPlayerProvider}: flex sizing like {@link AevocamPlayer},
 * and bubbled player keyboard shortcuts. `tabIndex={0}` so the player region is reachable
 * with Tab; {@link AevocamPlayer} also calls {@link AevocamPlayerActions.focusPlayerShell}
 * when the user clicks non-interactive chrome (e.g. the viewport). {@link AevocamViewport}
 * is not focusable. Interactive descendants call `stopPropagation` for keys they handle.
 */
export function AevocamPlayerShell({
  children,
  shellRef,
}: {
  children: ReactNode
  shellRef: RefObject<HTMLDivElement | null>
}) {
  const {
    loadingStage,
    isPlaying,
    playReverse,
    isFullScreen,
    zoomFit,
    currentIndex,
    stayBetweenInOut,
    inFrame,
    outFrame,
    playbackRepeat,
    actions,
  } = useAevocamPlayer()

  const handleKeyDown = useCallback(
    (ev: KeyboardEvent<HTMLDivElement>) => {
      if (loadingStage < 2) return
      switch (ev.key) {

        case ' ':
          ev.preventDefault()
          if (isPlaying) {
            actions.stop()
          } else {
            if (ev.shiftKey) {
              actions.setDirection(!playReverse)
            }
            actions.play(true)
          }
          break

        case 'ArrowLeft':
          ev.preventDefault()
          if (ev.shiftKey) {
            if (!zoomFit) {
              actions.nudgePan(+SHIFT_ARROW_PAN_PX, 0)
            }
          } else {
            actions.stop()
            actions.advance(-1)
          }
          break

        case 'ArrowRight':
          ev.preventDefault()
          if (ev.shiftKey) {
            if (!zoomFit) {
              actions.nudgePan(-SHIFT_ARROW_PAN_PX, 0)
            }
          } else {
            actions.stop()
            actions.advance(1)
          }
          break

        case 'ArrowUp':
          ev.preventDefault()
          if (ev.shiftKey) {
            if (!zoomFit) {
              actions.nudgePan(0, +SHIFT_ARROW_PAN_PX)
            }
          } else {
            actions.bumpFrameRateUp()
          }
          break

        case 'ArrowDown':
          ev.preventDefault()
          if (ev.shiftKey) {
            if (!zoomFit) {
              actions.nudgePan(0, -SHIFT_ARROW_PAN_PX)
            }
          } else {
            actions.bumpFrameRateDown()
          }
          break

        case 'Home':
          ev.preventDefault()
          actions.stop()
          actions.goFirst(stayBetweenInOut)
          break

        case 'End':
          ev.preventDefault()
          actions.stop()
          actions.goLast(stayBetweenInOut)
          break

        case '[':
          ev.preventDefault()
          if (currentIndex === inFrame) {
            actions.setInFrame(-1)
          } else {
            actions.setInFrame(currentIndex)
          }
          break

        case ']':
          ev.preventDefault()
          if (currentIndex === outFrame) {
            actions.setOutFrame(-1)
          } else {
            actions.setOutFrame(currentIndex)
          }
          break

        case '{':
          ev.preventDefault()
          actions.setInFrame(-1)
          break

        case '}':
          ev.preventDefault()
          actions.setOutFrame(-1)
          break
        
        case '_':
          ev.preventDefault()
          actions.zoomOut()
          break

        case '+':
          ev.preventDefault()
          actions.zoomIn()
          break
        
        case '(':
          ev.preventDefault()
          actions.zoomFit()
          break

        case ')':
          ev.preventDefault()
          actions.zoomActual()
          break

        case 'R':
          ev.preventDefault()
          switch (playbackRepeat) {
            case 'single':
              actions.setPlaybackRepeat('loop')
              break
            case 'loop':
              actions.setPlaybackRepeat('oscillate')
              break
            default:
              actions.setPlaybackRepeat('single')
          }
          break

        case 'T':
          ev.preventDefault()
          actions.openPhotoInNewTab()
          break

        case 'f':
          ev.preventDefault()
          void actions.fullScreen()
          break

        default:
          break
      }
    },
    [
      loadingStage,
      actions,
      isPlaying,
      playReverse,
      zoomFit,
      currentIndex,
      inFrame,
      outFrame,
      stayBetweenInOut,
      playbackRepeat,
    ],
  )

  return (
    <div
      ref={shellRef}
      className={`AevocamPlayerShell${isFullScreen ? ' FullScreen' : ''}`}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  )
}
